import React, { useState } from "react";
import {
  Box, TextField, Button, Typography, IconButton, MenuItem,
  Alert, Paper, Divider, useTheme,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import { createAccount, ACCOUNT_TYPES, ACCOUNT_TYPE_LABELS } from "../../utils/models/accounting/chartOfAccountsModel";

const ChartOfAccountsForm = ({ account, onSave, onCancel }) => {
  const theme = useTheme();
  const { add, update } = useAuditFirestore("chartOfAccounts");
  const { data: accounts } = useCollection("chartOfAccounts");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState(() => {
    if (account) return { ...account };
    return createAccount();
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code) { setError("El código es obligatorio"); return; }
    if (!formData.name) { setError("El nombre es obligatorio"); return; }
    setLoading(true);
    setError("");
    try {
      const data = { ...formData, updatedAt: new Date().toISOString() };
      if (account?.id) {
        await update(account.id, data);
      } else {
        await add({ ...data, createdAt: new Date().toISOString(), isActive: true });
      }
      onSave();
    } catch (err) {
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "12px", height: 44 },
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "24px", border: "1px solid", borderColor: theme.palette.divider }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight={800}>{account?.id ? "Editar Cuenta" : "Nueva Cuenta"}</Typography>
        <IconButton onClick={onCancel}><Close /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <TextField label="Código" name="code" value={formData.code} onChange={handleChange} required size="small" sx={{ ...fieldSx, flex: 1, minWidth: 150 }} />
          <TextField select label="Tipo" name="type" value={formData.type} onChange={handleChange} required size="small" sx={{ ...fieldSx, flex: 1, minWidth: 200 }}>
            {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField label="Nombre" name="name" value={formData.name} onChange={handleChange} required size="small" fullWidth sx={{ ...fieldSx, mb: 3 }} />
        <TextField select label="Cuenta Padre" name="parentCode" value={formData.parentCode || ""} onChange={handleChange} size="small" fullWidth sx={{ ...fieldSx, mb: 3 }}>
          <MenuItem value="">Ninguna (cuenta raíz)</MenuItem>
          {accounts?.filter(a => a.isActive !== false && a.id !== account?.id).map((a) => (
            <MenuItem key={a.id} value={a.code}>{a.code} - {a.name}</MenuItem>
          ))}
        </TextField>
        <TextField label="Descripción" name="description" value={formData.description} onChange={handleChange} size="small" multiline rows={2} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" }, mb: 3 }} />

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={onCancel} sx={{ borderRadius: "12px", textTransform: "none" }}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading} startIcon={<Save />} sx={{ borderRadius: "12px", textTransform: "none", boxShadow: "none" }}>
            {loading ? "Guardando..." : account?.id ? "Actualizar" : "Crear Cuenta"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ChartOfAccountsForm;